// src/pages/AllGames.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAllGames } from "../hooks/useAllGames";
import AllGameCard from "../components/AllGameCard";

export default function AllGames() {
  const navigate = useNavigate();
  const { games, loading } = useAllGames();

  const [query, setQuery] = useState("");
  const [genre, setGenre] = useState("All");
  const [tag, setTag] = useState("All");
  const [web3Only, setWeb3Only] = useState(false);
  const [web2Only, setWeb2Only] = useState(false);

  const genres = useMemo(() => {
    const set = new Set();
    games.forEach((g) => g.genre && set.add(g.genre));
    return ["All", ...Array.from(set).sort()];
  }, [games]);

  const tags = useMemo(() => {
    const set = new Set();
    games.forEach((g) => (g.tags || []).forEach((t) => set.add(t)));
    return ["All", ...Array.from(set).sort()];
  }, [games]);

  const results = useMemo(() => {
    let list = [...games];

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((g) => {
        const haystack = [
          g.title,
          g.description,
          g.genre,
          ...(g.tags || []),
          g.source,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (genre !== "All") list = list.filter((g) => g.genre === genre);
    if (tag !== "All") list = list.filter((g) => (g.tags || []).includes(tag));

    if (web3Only) list = list.filter((g) => g.isWeb3);
    if (web2Only) list = list.filter((g) => !g.isWeb3);
    



    return list;
  }, [games, query, genre, tag, web3Only, web2Only]);

  return (
    <div className="grid gap-6">
      <h1 className="text-3xl font-bold tracking-tight">All Games</h1>
    {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-slate-900/60 border border-slate-800 rounded-2xl px-4 py-3 shadow-sm shadow-emerald-500/10">

        {/* Search */}
        <input
          placeholder="Search games…"
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 min-w-[240px] text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {/* Genre */}
        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
        >
          {genres.map((g) => (
            <option key={g} value={g}>
              {g === "All" ? "All genres" : g}
            </option>
          ))}
        </select>

        {/* Tag */}
        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        >
          {tags.map((t) => (
            <option key={t} value={t}>
              {t === "All" ? "All tags" : t}
            </option>
          ))}
        </select>

        {/* ⭐ Web3 pill toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWeb3Only(!web3Only)}
            className={`
              relative inline-flex h-6 w-12 items-center rounded-full
              transition
              ${web3Only ? "bg-emerald-500" : "bg-slate-700"}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow
                transition
                ${web3Only ? "translate-x-6" : "translate-x-1"}
              `}
            />
          </button>
          <span className="text-sm text-slate-200">Web3 only</span>
        </div>

        {/* ⭐ Web2 pill toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWeb2Only(!web2Only)}
            className={`
              relative inline-flex h-6 w-12 items-center rounded-full
              transition
              ${web2Only ? "bg-emerald-500" : "bg-slate-700"}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow
                transition
                ${web2Only ? "translate-x-6" : "translate-x-1"}
              `}
            />
          </button>
          <span className="text-sm text-slate-200">Web2 only</span>
        </div>


      </div>


      {/* Results */}
      {loading && results.length === 0 ? (
        <div className="text-sm text-slate-400">Loading games…</div>
      ) : (
        <div className="space-y-3">
          {results.map((game) => (
            <AllGameCard
              key={game.id}
              game={game}
              onOpen={() => navigate(`/game/${game.id}`)}
              onPlay={() => navigate(`/play/${game.id}`)}
            />
          ))}
        </div>
      )}


        <div className="spacer" style={{height: 100}}></div>

    </div>
  );
}
